import React from "react";
import { Hourglass, Check, Clock } from "lucide-react";
import ParagraphTag from "../../../reusebale/Paragraph";

/**
 * Sum of additional charges (one-time, constant). Used to derive base from stored total.
 */
function getAdditionalChargesTotal(additionalCharges) {
  if (!Array.isArray(additionalCharges)) return 0;
  return additionalCharges.reduce((sum, c) => sum + Number(c.amount || 0), 0);
}

/**
 * Displays the saved rent schedule in "landlord base monthly rent" table format.
 * Stored totalRent = base + additional charges. Base is derived for display as totalRent - additionalCharges.
 */
const RentScheduleTable = ({ leaseDetails }) => {
  const schedule = leaseDetails?.rentSchedule;
  const unitSize = leaseDetails?.unitInfo?.size ? Number(leaseDetails.unitInfo.size) : null;
  const additionalChargesTotal = getAdditionalChargesTotal(leaseDetails?.additionalCharges);

  if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg px-4 pb-4 mt-4">
      <ParagraphTag
        content="Rent Schedule"
        classes="text-darkColor font-popinsSemiBold text-lg pt-4 text-center"
      />
      <ParagraphTag
        content="Landlord base monthly rent as described on attached Exhibit or as follows:"
        classes="text-darkColor/50 font-popinsMedium text-xs text-center mt-1"
      />
      <div className="mt-3 max-h-72 overflow-y-auto overflow-x-auto form-scrollbar border border-gray-300 rounded-b">
        <table className="w-full border-collapse text-sm font-popinsRegular">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="bg-bgColor">
              <th colSpan={2} className="border border-gray-300 px-2 py-2 text-left bg-bgColor">
                Dates
              </th>
              <th colSpan={2} className="border border-gray-300 px-2 py-2 text-left bg-bgColor">
                Rate per rentable square foot (optional)
              </th>
              <th colSpan={2} className="border border-gray-300 px-2 py-2 text-left bg-bgColor">
                Base Monthly Rent $
              </th>
              <th className="border border-gray-300 px-2 py-2 w-10 text-center bg-bgColor" title="Status"></th>
            </tr>
            <tr className="bg-bgColor">
              <th className="border border-gray-300 px-2 py-1 text-left text-xs bg-bgColor">From</th>
              <th className="border border-gray-300 px-2 py-1 text-left text-xs bg-bgColor">To</th>
              <th className="border border-gray-300 px-2 py-1 text-left text-xs bg-bgColor">$/rsf / month</th>
              <th className="border border-gray-300 px-2 py-1 text-left text-xs bg-bgColor">$/rsf / year</th>
              <th className="border border-gray-300 px-2 py-1 text-left text-xs bg-bgColor">Base</th>
              <th className="border border-gray-300 px-2 py-1 text-left text-xs bg-bgColor">Total</th>
              <th className="border border-gray-300 px-2 py-1 text-center text-xs w-10 bg-bgColor"></th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((row, index) => {
              const totalMonthlyAmount = Number(row.totalRent || 0);
              const baseMonthlyRent = Math.max(0, totalMonthlyAmount - additionalChargesTotal);
              const monthlyRsf = unitSize && unitSize > 0 ? totalMonthlyAmount / unitSize : null;
              const annualRsf = monthlyRsf != null ? monthlyRsf * 12 : null;
              return (
                <tr key={row.id || row.fromDate + row.toDate || index}>
                  <td className="border border-gray-300 px-2 py-2">{row.fromDate || "—"}</td>
                  <td className="border border-gray-300 px-2 py-2">{row.toDate || "—"}</td>
                  <td className="border border-gray-300 px-2 py-2">
                    {monthlyRsf != null ? `$${monthlyRsf.toFixed(2)}` : "—"}
                  </td>
                  <td className="border border-gray-300 px-2 py-2">
                    {annualRsf != null ? `$${annualRsf.toFixed(2)}` : "—"}
                  </td>
                  <td className="border border-gray-300 px-2 py-2">
                    ${baseMonthlyRent.toFixed(2)}
                  </td>
                  <td className="border border-gray-300 px-2 py-2">
                    ${totalMonthlyAmount.toFixed(2)}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 w-10 text-center">
                    {(() => {
                      const status = String(row.status ?? "").toLowerCase();
                      if (status === "complete" || status === "completed") {
                        return <Check className="inline-block w-4 h-4 text-green-600" aria-label="Complete" />;
                      }
                      if (status === "processing") {
                        return <Hourglass className="inline-block w-4 h-4 text-amber-500" aria-label="Processing" />;
                      }
                      return <Clock className="inline-block w-4 h-4 text-gray-400" aria-label="Pending" />;
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RentScheduleTable;
